import { Directive, Input, OnDestroy, OnInit, TemplateRef, ViewContainerRef } from '@angular/core';
import { combineLatest, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  AuthorizationService,
  PermissionStatus
} from 'src/app/_core/_services/authorization.service';

/**
 * Structural directive that shows the host element only when the current user
 * holds the required permission(s).  Reactively re-evaluates whenever the
 * permission set or load status changes.
 *
 * Behaviour by status:
 *   'idle' | 'loading'  →  element stays hidden (no flash); guard waits for ready$
 *   'loaded'            →  standard permission check; superadmin always passes
 *   'error'             →  denies unless fallbackRoles is provided and matches user role
 *
 * Single permission:
 *   <button *appHasPermission="'workforce.view'">…</button>
 *
 * Multiple (any one is enough, default):
 *   <div *appHasPermission="['users.read', 'users.write']">…</div>
 *
 * Require ALL listed permissions:
 *   <div *appHasPermission="['billing.read', 'billing.write']; mode: 'all'">…</div>
 *
 * Migration fallback — show if user lacks the permission but has one of these roles:
 *   <button *appHasPermission="'ops.manage'; fallbackRoles: ['operationsmanager']">…</button>
 */
@Directive({ selector: '[appHasPermission]' })
export class HasPermissionDirective implements OnInit, OnDestroy {
  @Input('appHasPermission') permission: string | string[] | null | undefined;
  @Input('appHasPermissionMode') mode: 'any' | 'all' = 'any';

  /**
   * If the permission check fails but the user holds one of these role names,
   * the element is shown anyway. Useful during the migration period when
   * permission strings may not yet exist in the backend.
   */
  @Input('appHasPermissionFallbackRoles') fallbackRoles: string[] = [];

  private hasView = false;
  private destroy$ = new Subject<void>();

  constructor(
    private templateRef: TemplateRef<any>,
    private viewContainer: ViewContainerRef,
    private authz: AuthorizationService
  ) {}

  ngOnInit(): void {
    combineLatest([this.authz.permissions$, this.authz.status$])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([, status]) => this.updateView(status));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private updateView(status: PermissionStatus): void {
    // While permissions haven't settled yet, leave the DOM untouched.
    // On mount hasView is false, so the element simply stays hidden until loaded.
    if (status === 'idle' || status === 'loading') return;

    const perms = Array.isArray(this.permission)
      ? this.permission
      : this.permission
        ? [this.permission]
        : [];

    // No permission specified at all (null/undefined/[]) - open to any
    // authenticated user, e.g. Home/Settings in the sidebar. Distinct from
    // "permission specified but not granted", which still denies.
    let granted =
      perms.length === 0 ||
      (this.mode === 'all'
        ? this.authz.hasAllPermissions(perms)
        : this.authz.hasAnyPermission(perms));

    // On error or when the named permission isn't in the set, try the fallback role list.
    if (!granted && this.fallbackRoles.length > 0) {
      granted = this.authz.hasAnyCurrentRole(this.fallbackRoles);
    }

    if (granted && !this.hasView) {
      this.viewContainer.createEmbeddedView(this.templateRef);
      this.hasView = true;
    } else if (!granted && this.hasView) {
      this.viewContainer.clear();
      this.hasView = false;
    }
  }
}
